import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AuthService from '../services/AuthService';
import jwt_decode from 'jwt-decode';
import apiRequest from '../services/apiClient';

const UserMenu = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [userPhoto, setUserPhoto] = useState(null);
  const [photoError, setPhotoError] = useState(false);

  // Forzar actualización cuando cambien los datos
  useEffect(() => {
    const handleStorageChange = () => {
      setRefreshTrigger(prev => prev + 1);
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Cargar la foto del usuario
  useEffect(() => {
    const loadUserPhoto = async () => {
      try {
        setPhotoError(false); // Reset error state
        const photoUrl = await AuthService.getUserPhoto();
        if (photoUrl) {
          setUserPhoto(photoUrl);
        } else {
          setPhotoError(true);
        }
      } catch (error) {
        console.error('Error cargando foto del usuario:', error);
        setPhotoError(true);
        setUserPhoto(null);
      }
    };

    loadUserPhoto();
  }, [refreshTrigger]);

  const handleLogout = async () => {
    try {
      // Cerrar el menú primero
      setIsOpen(false);
      
      // Intentar hacer logout en el servidor
      await AuthService.Logout();
    } catch (error) {
      // Si hay error, aún así limpiamos el localStorage
      console.error('Logout error:', error);
    } finally {
      // Limpiar datos locales completamente
      localStorage.removeItem('x-auth-token');
      localStorage.removeItem('userInfo');
      localStorage.removeItem('allowedCompanies'); // Limpiar empresas permitidas
      
      // Redirigir al login
      window.location.href = '/';
    }
  };

  // Obtener información del usuario desde el token
  const getCurrentUserInfo = () => {
    const token = localStorage.getItem('x-auth-token');
    
    if (!token) {
      return { company: 'Empresa', name: 'Usuario', companies: [] };
    }
    
    try {
      const tokenData = JSON.parse(token);
      const decodedJWT = jwt_decode(tokenData.accessToken);
      
      // El JWT tiene la estructura directa: companyId, companyName, companyCommercialName
      
      // Obtener empresas permitidas desde localStorage para obtener nombres reales
      const allowedCompanies = JSON.parse(localStorage.getItem("allowedCompanies") || "[]");
      
      // Buscar la empresa actual en las empresas permitidas
      let currentCompanyName = 'Empresa';
      if (decodedJWT.companyId && allowedCompanies.length > 0) {
        const currentCompany = allowedCompanies.find(comp => comp.company.id === decodedJWT.companyId);
        if (currentCompany) {
          currentCompanyName = currentCompany.company.commercialName || currentCompany.company.name;
        } else {
          currentCompanyName = decodedJWT.companyCommercialName || decodedJWT.companyName || 'Empresa';
        }
      } else if (decodedJWT.companyCommercialName || decodedJWT.companyName) {
        currentCompanyName = decodedJWT.companyCommercialName || decodedJWT.companyName;
      } else {
      }
      
      const result = {
        company: currentCompanyName,
        companyId: decodedJWT.companyId || null,
        name: decodedJWT.fullName || decodedJWT.name || 'Usuario',
        companies: [] // Las empresas se obtienen del localStorage, no del JWT
      };
      
      return result;
    } catch (error) {
      console.error('Error parsing token:', error);
      return { company: 'Empresa', name: 'Usuario', companies: [] };
    }
  };

  const { company: currentCompany, companyId: currentCompanyId, name: userName, companies } = getCurrentUserInfo();
  const initials = userName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  // Función para manejar errores de imagen
  const handleImageError = () => {
    console.log('Error cargando imagen del usuario, usando fallback');
    setPhotoError(true);
    setUserPhoto(null);
  };

  // Función para renderizar avatar con fallback
  const renderAvatar = (size, textSize) => {
    if (userPhoto && !photoError) {
      return (
        <img 
          src={userPhoto} 
          alt={userName}
          className="w-full h-full object-cover rounded-full"
          onError={handleImageError}
        />
      );
    } else {
      return (
        <span className={`${textSize} font-semibold text-gray-600`}>
          {initials}
        </span>
      );
    }
  };

  const handleCompanyChange = async (company) => {
    
    try {
      // Cerrar el menú primero
      setIsOpen(false);
      
      // Obtener información del usuario actual
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const token = localStorage.getItem('x-auth-token');
      
      
      if (!token) {
        console.error('No token found');
        alert('No se encontró el token de autenticación');
        return;
      }
      
      // Decodificar el token para obtener la información del usuario
      const tokenData = JSON.parse(token);
      
      // Decodificar el JWT accessToken
      const decodedJWT = jwt_decode(tokenData.accessToken);
      
      // Buscar accesses en diferentes ubicaciones posibles
      const user = decodedJWT.user || decodedJWT || {};
      
      // Crear el objeto access con los parámetros necesarios
      const accessUserId = decodedJWT.primarysid; // ID del usuario
      
      // Obtener las empresas permitidas desde localStorage
      const allowedCompanies = JSON.parse(localStorage.getItem("allowedCompanies") || "[]");
      
      if (!allowedCompanies || allowedCompanies.length === 0) {
        console.error('No allowed companies found in localStorage');
        alert('No se encontraron empresas permitidas. Por favor, inicie sesión nuevamente.');
        return;
      }
      
      // Buscar la empresa seleccionada (puede ser por name o commercialName)
      const selectedCompany = allowedCompanies.find(comp => 
        comp.company.name === company || comp.company.commercialName === company
      );
      
      if (!selectedCompany) {
        console.error('Company not found in allowed companies:', company);
        alert(`No se encontró la empresa: ${company}`);
        return;
      }
      
      const companyId = selectedCompany.company.id;
      
      // Crear el objeto access con la estructura esperada
      const access = {
        id: accessUserId,
        company: {
          id: companyId,
          name: selectedCompany.company.name,
          commercialName: selectedCompany.company.commercialName
        }
      };
      
      
      // Llamar al método ChangeCompany del AuthService
      await AuthService.ChangeCompany(access);
      
      
      // Actualizar la información del usuario en localStorage
      const updatedUserInfo = {
        ...userInfo,
        company: company,
        companyId: access.company.id
      };
      localStorage.setItem('userInfo', JSON.stringify(updatedUserInfo));
      
      
      // Recargar la página para aplicar los cambios
      window.location.reload();
      
    } catch (error) {
      console.error('Error changing company:', error);
      // Mostrar mensaje de error al usuario
      alert(`Error al cambiar de empresa: ${error.message}`);
    }
  };

  return (
    <div className="relative flex items-center gap-3">
      {/* Botón clickeable */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
      >
        <span className="text-sm font-medium">{currentCompany}</span>
      </button>

      {/* Avatar separado del botón */}
      <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden cursor-pointer hover:ring-2 hover:ring-blue-300 transition-all"
           onClick={() => setIsOpen(!isOpen)}>
        {renderAvatar('w-10 h-10', 'text-base')}
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-md shadow-lg border border-gray-200 z-50">
          <div className="py-3">
            {/* Nombre del Usuario */}
            <div className="px-4 py-2 border-b border-gray-200 flex items-center gap-3">
              <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
                {renderAvatar('w-12 h-12', 'text-lg')}
              </div>
              <div className="text-sm font-semibold text-gray-900">{userName}</div>
            </div>
            
            {/* Lista de Empresas */}
            <div className="py-2">
              {(() => {
                const allowedCompanies = JSON.parse(localStorage.getItem("allowedCompanies") || "[]");
                return allowedCompanies.map((access) => {
                  const isCurrentCompany = currentCompanyId === access.company.id;
                  return (
                    <button
                      key={access.company.id}
                      onClick={() => {
                        if (!isCurrentCompany) {
                          handleCompanyChange(access.company.commercialName);
                        }
                      }}
                      disabled={isCurrentCompany}
                      className={`w-full px-4 py-2 text-left text-sm transition-colors ${
                        isCurrentCompany 
                          ? 'bg-blue-50 text-blue-700 font-medium cursor-not-allowed' 
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {access.company.commercialName}
                    </button>
                  );
                });
              })()}
            </div>
            
            {/* Separador */}
            <div className="border-t border-gray-200"></div>
            
            {/* Logout */}
            <button
              onClick={handleLogout}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              {t('userMenu.logout')}
            </button>
          </div>
        </div>
      )}

      {/* Overlay para cerrar el menú */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default UserMenu;
